export interface SelectOption {
  readonly value: string;
  readonly label: string;
}

export interface ProblemCard {
  readonly image: string;
  readonly imageWidth: number;
  readonly imageHeight: number;
  readonly alt: string;
  readonly text: string;
}

export const PROBLEM_CARDS: readonly ProblemCard[] = [
  {
    image: 'images/card-students.webp',
    imageWidth: 306,
    imageHeight: 193,
    alt: 'Stack of books with an apple and ABC blocks',
    text: 'Students use AI to get answers — not to learn. Understanding is being replaced by copy-paste.',
  },
  {
    image: 'images/card-teachers.webp',
    imageWidth: 306,
    imageHeight: 193,
    alt: 'Smiling teacher holding an orange folder',
    text: 'There are not adequate tools to help teachers distinguish who learned and who let AI do the work.',
  },
  {
    image: 'images/card-administrators.webp',
    imageWidth: 306,
    imageHeight: 193,
    alt: 'Administrators and teachers collaborating with laptops and notes',
    text: 'Administrators are flying blind — grades show completion, not comprehension.',
  },
] as const;

export interface SolutionPillar {
  readonly icon: 'book' | 'shield' | 'chart';
  readonly title: string;
  readonly description: string;
}

export const SOLUTION_PILLARS: readonly SolutionPillar[] = [
  {
    icon: 'book',
    title: 'Personalized Learning at Scale',
    description:
      "Every student gets an individualized learning experience powered by AI, guided by their teacher's curriculum.",
  },
  {
    icon: 'shield',
    title: 'Teachers Stay in Control',
    description:
      'AI needs context, teachers control where that context comes from. They control what information is referenced and what objectives are taught.',
  },
  {
    icon: 'chart',
    title: 'Real Understanding, Not Just Grades',
    description:
      'Our platform measures whether students actually grasp concepts — not just whether they turned something in.',
  },
] as const;

export const CREDENTIALS = [
  'Supported by educators and faculty from UVU and Nebo',
  'FIELD STUDY WITH 4 PHD RESEARCHERS',
  '67+ EDUCATOR CONVERSATIONS',
  "BUILT ON BLOOM'S 2 SIGMA RESEARCH",
] as const;

export const PRICING_TIERS = [
  {
    title: 'Classroom',
    description: 'Perfect for individual teachers piloting AI-powered learning',
    price: 'Contact for pricing',
    ctaLabel: 'Contact Us',
    ctaHref: '/contact',
    popular: false,
  },
  {
    title: 'School',
    description:
      'Full school deployment with admin analytics and teacher tools',
    price: 'Contact for pricing',
    ctaLabel: 'Contact Us',
    ctaHref: '/contact',
    popular: true,
  },
  {
    title: 'District',
    description:
      'District-wide rollout with dedicated support and custom integration',
    price: 'Contact for pricing',
    ctaLabel: 'Contact Us',
    ctaHref: '/contact',
    popular: false,
  },
] as const;

export const PRICING_FEATURES = [
  'AI-powered student learning platform',
  'Teacher content management portal',
  'Administrator analytics dashboard',
  'LMS compatibility (Canvas, Google Classroom)',
  'Dedicated onboarding support',
  'Data privacy & security compliance',
] as const;

export const ROLE_OPTIONS: readonly SelectOption[] = [
  { value: 'teacher', label: 'Teacher / Educator' },
  { value: 'administrator', label: 'School Administrator' },
  { value: 'district_leader', label: 'District Leader' },
  { value: 'researcher', label: 'Researcher / Academic' },
  { value: 'other', label: 'Other' },
] as const;

export interface TeamMember {
  readonly name: string;
  readonly role: string;
  readonly initials: string;
  readonly image?: string;
  readonly linkedin?: string;
}

export const TEAM_MEMBERS: readonly TeamMember[] = [
  {
    name: 'Jonathan Craner',
    role: 'Chief Executive Officer & Founder',
    initials: 'JC',
    image: 'images/team-jonathan.webp',
  },
  {
    name: 'Brennan Campbell',
    role: 'Chief Financial Officer',
    initials: 'BC',
    image: 'images/team-brennan.webp',
  },
  {
    name: 'Michael Paris',
    role: 'Chief Technology Officer',
    initials: 'MP',
    image: 'images/team-michael.webp',
  },
  {
    name: 'Raine Petersen',
    role: 'Chief Systems Architect',
    initials: 'RP',
    image: 'images/team-raine.webp',
  },
  {
    name: 'Joseph Call',
    role: 'System Administrator',
    initials: 'JC',
    image: 'images/team-joseph.webp',
  },
] as const;

export interface Faq {
  readonly q: string;
  readonly a: string;
}

export const FAQS: readonly Faq[] = [
  {
    q: "What is Benjamin Bloom's 2 Sigma Problem?",
    a: 'In 1984, educational psychologist Benjamin Bloom discovered that one-on-one tutoring improved student performance by 2 standard deviations — outperforming 98% of traditionally taught students. LearnAI makes that personalized standard scalable for every classroom.',
  },
  {
    q: 'How does LearnAI ensure students actually learn rather than copying answers?',
    a: 'Unlike generic chatbots that output full answers, LearnAI uses guided Socratic methods that prompt critical thinking, diagnose learning gaps, and verify true comprehension before progressing.',
  },
  {
    q: 'How do teachers maintain control over the curriculum?',
    a: 'Teachers select what is taught, define instructional guardrails, and receive clear analytics detailing student progress, ensuring AI acts as a dedicated assistant rather than a replacement.',
  },
] as const;

export interface HowItWorksStep {
  readonly step: number;
  readonly title: string;
  readonly description: string;
}

export const HOW_IT_WORKS_STEPS: readonly HowItWorksStep[] = [
  {
    step: 1,
    title: 'Teacher uploads materials, selects concepts and facts',
    description:
      'Educators add source materials, syllabi, or chapter PDFs to their catalog and choose the precise concepts and learning objectives to cover.',
  },
  {
    step: 2,
    title:
      'Student has natural conversation with AI to learn concepts and facts',
    description:
      'Students engage in proactive Socratic dialogue that guides them through foundational knowledge at their individual pace.',
  },
  {
    step: 3,
    title:
      'AI determines level of understanding based on responses & sends data back',
    description:
      'The system diagnoses comprehension from conversational responses, identifying misconceptions and feeding real-time mastery analytics back to the teacher.',
  },
  {
    step: 4,
    title:
      'Student, now knowing the basics, has critical conversations with the teacher',
    description:
      'With foundational facts internalized, class time shifts to rich discussions, problem solving, and human-led critical thinking.',
  },
] as const;

export interface HowItWorksCallout {
  readonly title: string;
  readonly description: string;
  readonly image: string;
  readonly imageAlt: string;
  readonly imageWidth: number;
  readonly imageHeight: number;
}

export const HOW_IT_WORKS_CALLOUTS: readonly HowItWorksCallout[] = [
  {
    title: 'Proactive vs Reactive',
    description:
      'Have the tool guide the student and keep the conversation within guardrails created by the teacher/administrator.',
    image: 'images/how-it-works-chat.webp',
    imageAlt:
      'Interactive student chat interface showing guided Socratic conversation',
    imageWidth: 394,
    imageHeight: 852,
  },
  {
    title: 'Focus on Facts',
    description:
      'The Tool is designed to teach Facts and concepts, leave the critical thinking in the classroom with the teachers.',
    image: 'images/how-it-works-material.webp',
    imageAlt:
      'Teacher uploading source materials and defining curriculum concepts',
    imageWidth: 946,
    imageHeight: 1191,
  },
] as const;

export interface ProductPrinciple {
  readonly title: string;
  readonly subtitle: string;
  readonly description: string;
}

export const PRODUCT_PRINCIPLES: readonly ProductPrinciple[] = [
  {
    title: 'Theory Based Logic',
    subtitle:
      'Cognitive load theory + Socratic Questioning + Constructivist Learning Theory',
    description:
      'Designed around cognitive science principles to prevent information overload, prompt active student reasoning, and build knowledge through structured discovery.',
  },
  {
    title: 'Conversation Analysis',
    subtitle: 'Unstructured data reveals true understanding, not just grades',
    description:
      'Natural dialogue diagnostics capture conceptual misconceptions, depth of reasoning, and true comprehension that multiple-choice tests often miss.',
  },
  {
    title: 'Human Centered',
    subtitle:
      'We provide the tool, the teachers provide the content and control what is being taught',
    description:
      'Teachers are the instructional authority. The platform gives them full power over reference materials, learning objectives, and pedagogical guardrails.',
  },
  {
    title: 'Mentor not Teacher',
    subtitle:
      'The AI tool helps the students learn facts and concepts to then be explored with the teacher.',
    description:
      'The AI serves as a patient 1-on-1 mentor for building core knowledge, ensuring students arrive in the classroom prepared for higher-order inquiry.',
  },
  {
    title: 'Enhanced Learning',
    subtitle:
      'Personalized conversation to fit the students learning styles while providing real-time feedback to teachers',
    description:
      'Adaptive dialogue meets each student where they are, giving educators immediate visibility into student progress and class-wide trends.',
  },
] as const;
